/**
 * Application Paths
 *
 * Centralized path generation for consistent URL building across the app.
 * All paths are relative (no locale prefix) - use with locale-prefixed routes.
 */

// =============================================================================
// SEARCH PATHS
// =============================================================================

export type SearchTab = 'all' | 'watches' | 'straps' | 'eyewear' | 'stores';

/**
 * Generate search page URLs.
 */
export const searchPaths = {
  /**
   * All search results page
   * @param query - Optional search query
   */
  all: (query?: string) => `/search/all${query ? `?q=${encodeURIComponent(query)}` : ''}`,

  /**
   * Watches search results page
   * @param query - Optional search query
   */
  watches: (query?: string) => `/search/watches${query ? `?q=${encodeURIComponent(query)}` : ''}`,

  /**
   * Straps search results page
   * @param query - Optional search query
   */
  straps: (query?: string) => `/search/straps${query ? `?q=${encodeURIComponent(query)}` : ''}`,

  /**
   * Eyewear search results page
   * @param query - Optional search query
   */
  eyewear: (query?: string) => `/search/eyewear${query ? `?q=${encodeURIComponent(query)}` : ''}`,

  /**
   * Stores search results page
   * @param query - Optional search query
   */
  stores: (query?: string) => `/search/stores${query ? `?q=${encodeURIComponent(query)}` : ''}`,

  /**
   * Get search path for a specific tab
   * @param tab - The search tab
   * @param query - Optional search query
   */
  forTab: (tab: SearchTab, query?: string) => {
    const pathFn = searchPaths[tab];
    return pathFn(query);
  },
};

// =============================================================================
// PRODUCT PATHS
// =============================================================================

export const productPaths = {
  /**
   * All watches listing page
   */
  watches: () => '/watches',

  /**
   * Watch collection page
   * @param collectionSlug - The collection slug
   */
  watchCollection: (collectionSlug: string) => `/watches/${collectionSlug}`,

  /**
   * Watch product detail page
   * @param slug - The product slug (SKU)
   */
  watchPdp: (slug: string) => `/watches/p/${slug}`,

  /**
   * All straps listing page
   */
  straps: () => '/straps',

  /**
   * Strap product detail page
   * @param slug - The product slug (SKU)
   */
  strapPdp: (slug: string) => `/straps/p/${slug}`,

  /**
   * All eyewear listing page
   */
  eyewear: () => '/eyewear',

  /**
   * Eyewear product detail page
   * @param slug - The product slug (SKU)
   */
  eyewearPdp: (slug: string) => `/eyewear/p/${slug}`,
};

// =============================================================================
// STORE / BOUTIQUE PATHS
// =============================================================================

function buildAppointmentRootPath(countryCode?: string): string {
  const base = '/boutiques/appointment';
  if (!countryCode) {
    return base;
  }
  return `${base}?country=${countryCode.toLowerCase()}`;
}

export const boutiquePaths = {
  /**
   * Boutiques listing page. Locale prefix stays fixed; country is the last segment
   * (Breitling parity: /us-en/boutiques/ch).
   */
  listing: (countryCode?: string) =>
    countryCode ? `/boutiques/${countryCode.toLowerCase()}` : '/boutiques',

  /**
   * Boutique detail page (store resolved by globally unique slug).
   */
  detail: (slug: string) => `/boutiques/${slug}`,

  /**
   * Locale-level appointment page — unified booking form (Breitling: /stores/appointment/).
   * Optional country query preserves legacy /boutiques/{country}/appointment bookmarks.
   */
  appointmentRoot: buildAppointmentRootPath,

  /**
   * @deprecated Country segment removed — use appointmentRoot(countryCode). Kept for callers migrating links.
   */
  appointmentListing: buildAppointmentRootPath,

  /**
   * Boutique appointment booking page — dedicated form route (Breitling: /stores/appointment/…).
   */
  appointment: (slug: string) => `/boutiques/${slug}/appointment`,
};

export const storePaths = {
  /**
   * Store finder page
   */
  finder: () => '/boutiques',

  /**
   * Store detail page
   * @param slug - Boutique slug
   */
  detail: (slug: string) => boutiquePaths.detail(slug),
};

// =============================================================================
// ACCOUNT PATHS
// =============================================================================

export const accountPaths = {
  /**
   * Account dashboard
   */
  dashboard: () => '/account',

  /**
   * Account orders
   */
  orders: () => '/account/orders',

  /**
   * Account addresses
   */
  addresses: () => '/account/addresses',

  /**
   * Wishlist page
   */
  wishlist: () => '/account/wishlist',

  /**
   * Login page
   */
  login: () => '/login',

  /**
   * Register page
   */
  register: () => '/register',
};

// =============================================================================
// CHECKOUT PATHS
// =============================================================================

export const orderPaths = {
  /**
   * Pay-per-link payment page for an existing order (spec: /store/order/payment/[token]/).
   * Locale prefix is added by next-intl navigation.
   */
  payPerLinkPayment: (token: string) => `/store/order/payment/${encodeURIComponent(token)}/`,
};

export const checkoutPaths = {
  /**
   * Cart page
   */
  cart: () => '/cart',

  /**
   * Checkout flow
   */
  checkout: () => '/checkout',

  /**
   * Checkout delivery step
   */
  delivery: () => '/checkout/delivery',

  /**
   * Checkout payment step
   */
  payment: () => '/checkout/payment',

  /**
   * Checkout review step
   */
  review: () => '/checkout/review',

  /**
   * Order confirmation page
   * @param orderId - Saleor global order ID (case-sensitive; path segment preserved by proxy)
   */
  confirmation: (orderId: string) => `/store/order/confirmation/${encodeURIComponent(orderId)}`,
};
